import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Button } from '@/app/components/ui/button';
import { Label } from '@/app/components/ui/label';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { TextAnnotation, BlockSystem, PipeType } from '@/app/types/cad';

interface TextAnnotationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  annotation: TextAnnotation | null;
  onSave: (annotation: Partial<TextAnnotation>) => void;
  systems: BlockSystem[];
  pipeTypes: PipeType[];
  projectName: string;
}

export function TextAnnotationDialog({
  open,
  onOpenChange,
  annotation,
  onSave,
  systems,
  pipeTypes,
  projectName,
}: TextAnnotationDialogProps) {
  const [text, setText] = useState('');
  const [fontSize, setFontSize] = useState(16);
  const [color, setColor] = useState('#000000');
  const [rotation, setRotation] = useState(0);
  const [alignment, setAlignment] = useState<'left' | 'center' | 'right'>('left');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [backgroundOpacity, setBackgroundOpacity] = useState(0.9);
  const [showBackground, setShowBackground] = useState(false);

  useEffect(() => {
    if (annotation) {
      setText(annotation.text || '');
      setFontSize(annotation.fontSize || 16);
      setColor(annotation.color || '#000000');
      setRotation((annotation.rotation || 0) * (180 / Math.PI)); // Convert to degrees
      setAlignment(annotation.alignment || 'left');
      setBackgroundColor(annotation.backgroundColor || '#ffffff');
      setBackgroundOpacity(annotation.backgroundOpacity ?? 0.9);
      setShowBackground(!!annotation.backgroundColor);
    } else {
      setText('');
      setFontSize(16);
      setColor('#000000');
      setRotation(0);
      setAlignment('left');
      setBackgroundColor('#ffffff');
      setBackgroundOpacity(0.9);
      setShowBackground(false);
    }
  }, [annotation, open]);

  const handleSave = () => {
    onSave({
      text,
      fontSize,
      color,
      rotation: rotation * (Math.PI / 180), // Convert to radians
      alignment,
      backgroundColor: showBackground ? backgroundColor : undefined,
      backgroundOpacity: showBackground ? backgroundOpacity : undefined,
    });
    onOpenChange(false);
  };

  const insertPlaceholder = (placeholder: string) => {
    setText((prev) => prev + placeholder);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {annotation ? 'Text bearbeiten' : 'Text hinzufügen'}
          </DialogTitle>
          <DialogDescription>
            Text mit Platzhaltern für Projekt-Parameter
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Text Input */}
          <div className="grid gap-2">
            <Label htmlFor="text">Text</Label>
            <Textarea
              id="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Text eingeben..."
              rows={4}
              className="font-mono"
            />
          </div>

          {/* Placeholders */}
          <div className="grid gap-2">
            <Label>Platzhalter einfügen</Label>
            <div className="grid grid-cols-2 gap-2">
              {/* Project */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => insertPlaceholder('{project.name}')}
              >
                Projektname
              </Button>

              {/* Systems */}
              {systems.length > 0 && (
                <div className="col-span-2">
                  <Label className="text-xs text-muted-foreground">Systeme:</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {systems.map((system) => (
                      <Button
                        key={system.id}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertPlaceholder(`{system:${system.id}.name}`)}
                        className="justify-start truncate"
                        title={system.name}
                      >
                        <div
                          className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                          style={{ backgroundColor: system.color }}
                        />
                        <span className="truncate">{system.name}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Pipe Types */}
              {pipeTypes.length > 0 && (
                <div className="col-span-2">
                  <Label className="text-xs text-muted-foreground">Linientypen:</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {pipeTypes.map((pipeType) => (
                      <Button
                        key={pipeType.id}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertPlaceholder(`{pipetype:${pipeType.id}.name}`)}
                        className="justify-start truncate"
                        title={`${pipeType.name} (DN ${pipeType.dn})`}
                      >
                        <div
                          className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                          style={{ backgroundColor: pipeType.color }}
                        />
                        <span className="truncate">{pipeType.name}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Für System-Attribute: <code className="bg-muted px-1 rounded">{'{'}system:ID.attribute.NAME{'}'}</code>
            </p>
          </div>

          {/* Font Size */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="fontSize">Schriftgröße</Label>
              <Input
                id="fontSize"
                type="number"
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                min={8}
                max={72}
              />
            </div>

            {/* Rotation */}
            <div className="grid gap-2">
              <Label htmlFor="rotation">Drehung (°)</Label>
              <Input
                id="rotation"
                type="number"
                value={rotation}
                onChange={(e) => setRotation(Number(e.target.value))}
                step={15}
              />
            </div>
          </div>

          {/* Color and Alignment */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="color">Textfarbe</Label>
              <div className="flex gap-2">
                <Input
                  id="color"
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-16 h-10"
                />
                <Input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="alignment">Ausrichtung</Label>
              <Select value={alignment} onValueChange={(val) => setAlignment(val as any)}>
                <SelectTrigger id="alignment">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Links</SelectItem>
                  <SelectItem value="center">Zentriert</SelectItem>
                  <SelectItem value="right">Rechts</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Background */}
          <div className="grid gap-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showBackground"
                checked={showBackground}
                onChange={(e) => setShowBackground(e.target.checked)}
                className="w-4 h-4"
              />
              <Label htmlFor="showBackground">Hintergrund anzeigen</Label>
            </div>

            {showBackground && (
              <div className="grid grid-cols-2 gap-4 pl-6">
                <div className="grid gap-2">
                  <Label htmlFor="bgColor">Hintergrundfarbe</Label>
                  <div className="flex gap-2">
                    <Input
                      id="bgColor"
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="w-16 h-10"
                    />
                    <Input
                      type="text"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="bgOpacity">Deckkraft</Label>
                  <Input
                    id="bgOpacity"
                    type="number"
                    value={backgroundOpacity}
                    onChange={(e) => setBackgroundOpacity(Number(e.target.value))}
                    min={0}
                    max={1}
                    step={0.1}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleSave}>Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
